import { useEffect, ReactNode, useRef } from 'react';
import { Sidebar, Box } from '@nimbus-ds/components';
import { AppShell } from '@nimbus-ds/patterns';
import { MOBILE_BREAKPOINT } from 'App/constants/breakpoints';
import { NUVEMENVIO_ADMIN_BLOCK } from 'App/featuresFlags';
import { useNavegate } from 'App/hooks';
import { useResponsive } from 'commons/components';
import useWindowSize from 'commons/hooks/useWindowSize';
import { useGetCountry, useGetTags } from 'domains/Auth/hooks';
import { CHECKOUT_ROUTES } from 'domains/Billing/Checkout/checkoutRoutes';
import {
  TRANSACTION_FEES_BASE_ROUTE,
  TRANSACTION_FEES_ROUTES,
} from 'domains/Billing/TransactionFees/transactionFeesRoutes';
import { DASHBOARD_BASE_PATH } from 'domains/Dashboard';
import { NUVEM_ENVIO_ROUTES } from 'domains/NuvemEnvioCommons/nuvemEnvioRoutes';
import { ExpirationBanner } from '../ExpirationBanner/ExpirationBanner';
import { useDesktopMenuContext } from '../Menu/DesktopMenuProvider';
import { useNavTabs } from '../Navtabs';
import { TopAdBar } from '../TopAdBar';
import { useTopAdBar } from '../TopAdBar/useTopAdBar';
import TopBar from '../Topbar';
import useTopBar from '../Topbar/useTopBar';
import './AppLayout.scss';

const TRANSACTION_FEES_ROUTES_VALUES = [
  TRANSACTION_FEES_BASE_ROUTE,
  ...Object.values(TRANSACTION_FEES_ROUTES),
];

const NUVEM_ENVIO_ROUTES_VALUES = [...Object.values(NUVEM_ENVIO_ROUTES)];

const createRoutePattern = (route: string): RegExp =>
  new RegExp(`^${route.replace(/:\w+/g, '[^/]+')}$`);

const UNLOCKED_ROUTE_PATTERNS = [
  new RegExp(`^${DASHBOARD_BASE_PATH}`),
  ...NUVEM_ENVIO_ROUTES_VALUES.map((route) => new RegExp(`^${route}`)),
  createRoutePattern(CHECKOUT_ROUTES.choosePaymentMethodForPayOrder),
  createRoutePattern(CHECKOUT_ROUTES.payWithCreditCard),
  ...TRANSACTION_FEES_ROUTES_VALUES.map(createRoutePattern),
];

interface AppLayoutInterface {
  children: ReactNode;
  menu: ReactNode;
  navtabs: ReactNode;
}

function AppLayout({ children, menu, navtabs }: Readonly<AppLayoutInterface>) {
  const { width } = useWindowSize();
  const { isShowSideBar, hideSideBar } = useNavTabs();
  const { pathname, goTo } = useNavegate();
  const { isDesktop } = useResponsive();
  const { isShowMenu } = useDesktopMenuContext();

  const {
    isShowTopbar,
    height: topBarHeight,
    setHeight: setTopBarHeight,
  } = useTopBar();
  const { height: topAddBarHeight, setHeight: setTopAdBarHeight } =
    useTopAdBar();

  const tags = useGetTags();
  const country = useGetCountry();
  const shouldShowNavtabs = width <= MOBILE_BREAKPOINT;
  const refTopBar = useRef<HTMLDivElement>(null);
  const refTopAdBar = useRef<HTMLDivElement>(null);

  /*
    AQUI ESTAMOS VALIDANDO SE O MERCHANT TEM A TAG, 
    CASO TENHA PRECISAMOS REDIRECIONAR PARA A 
    TELA DE BILLING DO APLICATIVO NUVEM ENVIO.

    MOTIVO: PARA FORÇAR O MERCHANT EFETUAR O PAGAMENTO
    PENDENTE DOS ENVIOS FEITOS

    =====

    AQUÍ ESTAMOS VALIDANDO SI EL COMERCIANTE TIENE LA ETIQUETA,
    SI LO TIENES, NECESITAMOS REDIRIGIRLO A LA
    PANTALLA DE FACTURACIÓN DEL ENVÍO DE LA APLICACIÓN EN LA NUBE.

    MOTIVO: OBLIGAR AL COMERCIO A REALIZAR EL PAGO
    PENDIENTES DE ENVÍOS REALIZADOS
  */

  useEffect(() => {
    if (tags.includes(NUVEMENVIO_ADMIN_BLOCK)) {
      hideSideBar();

      const shouldRedirect = !UNLOCKED_ROUTE_PATTERNS.some((pattern) =>
        pattern.test(pathname),
      );

      if (shouldRedirect) {
        goTo(NUVEM_ENVIO_ROUTES[country]);
      }
    }
  }, [hideSideBar, tags, pathname, goTo, country]);

  /**
   * This effect retrieves the height of the header and updates
   * the `useTopBar` state.
   * The stored height is later used in `IonPageStratus` to adjust
   * the page header's translation, aligning it with the toolbar's position.
   */
  useEffect(() => {
    const topBar = refTopBar.current;
    const topAdBar = refTopAdBar.current;

    let resizeObserverTopBar;
    let resizeObserverTopAdBar;
    if (topBar) {
      resizeObserverTopBar = new ResizeObserver(() => {
        setTopBarHeight(topBar.offsetHeight);
      });
      resizeObserverTopBar.observe(topBar);
    }
    if (topAdBar) {
      resizeObserverTopAdBar = new ResizeObserver(() => {
        setTopAdBarHeight(topAdBar.offsetHeight);
      });
      resizeObserverTopAdBar.observe(topBar);
    }

    return () => {
      resizeObserverTopBar?.disconnect();
      resizeObserverTopAdBar?.disconnect();
    };
  }, [setTopAdBarHeight, setTopBarHeight, isDesktop, pathname]);

  return (
    <AppShell menu={!shouldShowNavtabs && isShowMenu && menu}>
      {shouldShowNavtabs && (
        <div className="stratus--app-layout-sidebar">
          <Sidebar
            open={isShowSideBar}
            onRemove={hideSideBar}
            position="right"
            maxWidth="305px"
            needRemoveScroll
          >
            {menu}
          </Sidebar>
        </div>
      )}
      {isDesktop && (
        <div
          className="stratus--app-layout-header"
          style={{
            transform: `translateY(${
              isShowTopbar ? 0 : -(topBarHeight + topAddBarHeight)
            }px)`,
          }}
        >
          <div ref={refTopAdBar}>
            <TopAdBar />
          </div>
          <div ref={refTopBar}>
            <TopBar />
          </div>
        </div>
      )}
      {!isShowMenu ? (
        <Box height="calc(100vh - 50px)">{children}</Box>
      ) : (
        children
      )}
      {shouldShowNavtabs ? navtabs : <ExpirationBanner />}
    </AppShell>
  );
}

export default AppLayout;
